import { X } from "lucide-react";

import { SensitiveMoney } from "@/components/common/Sensitive";
import { SurchargeCode } from "@/components/common/SurchargeCode";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { formatPreCarriageLabel, formatRouteLabel } from "@/lib/formatPort";
import { surchargeNoticeOverlayByCode } from "@/lib/surchargeNoticeDisplay";
import {
  isBlBasis,
  surchargeCategoryKey,
  surchargeDisplayOptionsFromRateLine,
  surchargesForLineDisplay,
  type SurchargeCategoryKey,
} from "@/lib/surchargeDisplay";
import { cn } from "@/lib/utils";
import { salesPriceBreakdown } from "@/lib/salesPrice";
import { salesInputFromRateLineRow } from "@/lib/salesPriceAdapters";
import {
  RATE_LINE_OVER_MAX_CLASS,
  rateLineGrossExceedsCap,
  rateLineRowWeightCapInput,
} from "@/lib/rateLineWeightCap";
import { formatKgAsTons } from "@/lib/ratePolicies";
import { type RateLineRow, type RateSurcharge } from "@/types/rate";
import { RateLinePolicySections } from "./RatePolicySections";
import { SurchargeAmountWithNotice } from "./SurchargeAmountWithNotice";
import { useRateLookupCargoWeight } from "./useRateLookupCargoWeight";

interface RateSurchargeCardProps {
  line: RateLineRow;
  onClose: () => void;
  showLocodes: boolean;
  className?: string;
}

const CATEGORY_SECTIONS: { key: SurchargeCategoryKey; label: string }[] = [
  { key: "ocean", label: "Ocean" },
  { key: "local", label: "Local" },
  { key: "other", label: "Other" },
  { key: "inclusive", label: "Inclusive (in freight)" },
];

function groupSurcharges(surcharges: RateSurcharge[]) {
  return CATEGORY_SECTIONS.map((section) => {
    const items = surcharges.filter(
      (s) => surchargeCategoryKey(s.category) === section.key,
    );
    return {
      ...section,
      container: items.filter((s) => !isBlBasis(s.basis)),
      bl: items.filter((s) => isBlBasis(s.basis)),
    };
  }).filter((section) => section.container.length > 0 || section.bl.length > 0);
}

function SurchargeRow({
  surcharge,
  basisSuffix,
  overlayByCode,
  equipmentSize,
}: {
  surcharge: RateSurcharge;
  basisSuffix?: string;
  overlayByCode: ReturnType<typeof surchargeNoticeOverlayByCode>;
  equipmentSize?: string | null;
}) {
  return (
    <tr className="border-b border-border/40 last:border-0">
      <td className="py-2 pr-3 font-medium">
        <SurchargeCode code={surcharge.code} />
      </td>
      <td className="py-2 pr-3 tabular-nums">
        {basisSuffix ? (
          <>
            <SensitiveMoney amount={surcharge.amount} />
            <span className="ml-1 text-xs text-muted-foreground">
              {basisSuffix}
            </span>
          </>
        ) : (
          <SurchargeAmountWithNotice
            code={surcharge.code}
            amount={surcharge.amount}
            currency={surcharge.currency}
            basis={surcharge.basis}
            equipmentSize={equipmentSize}
            overlayByCode={overlayByCode}
          />
        )}
      </td>
      <td className="py-2 pr-3 text-muted-foreground">{surcharge.currency}</td>
      <td className="py-2 text-muted-foreground">{surcharge.basis}</td>
    </tr>
  );
}

export function RateSurchargeCard({
  line,
  onClose,
  showLocodes,
  className,
}: RateSurchargeCardProps) {
  const via = formatPreCarriageLabel(
    line.pre_carriage_pol,
    line.pre_carriage_mot,
    showLocodes,
  );
  const salesBreakdown = salesPriceBreakdown(salesInputFromRateLineRow(line));
  const displayOptions = surchargeDisplayOptionsFromRateLine(line);
  const displaySurcharges = surchargesForLineDisplay(
    line.surcharges,
    displayOptions,
  );
  const overlayByCode = surchargeNoticeOverlayByCode(line.notice_overlays);
  const groups = groupSurcharges(displaySurcharges);
  const isLcl = line.load_type === "lcl";
  const freightBasis = line.freight_basis ?? "W/M";
  const { resolveGrossKg } = useRateLookupCargoWeight();
  const overMax = rateLineGrossExceedsCap({
    ...rateLineRowWeightCapInput(line),
    gross_kg: resolveGrossKg(
      line.carrier,
      line.equipment_size,
      line.equipment_type,
    ),
  });

  return (
    <section
      className={cn("glass-panel relative p-4 lg:p-5", className)}
    >
      {overMax?.exceeds && (
        <div
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-0 rounded-[inherit]",
            RATE_LINE_OVER_MAX_CLASS,
          )}
        />
      )}
      <div className="relative mb-3 flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold">
            {formatRouteLabel(
              line.origin_port,
              line.destination_port,
              showLocodes,
            )}
          </h3>
          {via && (
            <p className="text-xs text-muted-foreground">{via}</p>
          )}
          <p className="text-xs text-muted-foreground">
            {line.equipment_summary}
            <span className="mx-2 text-border">·</span>
            Ocean all-in <SensitiveMoney amount={salesBreakdown.oceanAllIn} />
            {salesBreakdown.localsPerContainer > 0 && (
              <>
                <span className="mx-2 text-border">·</span>
                Locals <SensitiveMoney amount={salesBreakdown.localsPerContainer} />
              </>
            )}
            {salesBreakdown.blOnce > 0 && (
              <>
                <span className="mx-2 text-border">·</span>
                <SensitiveMoney amount={salesBreakdown.blOnce} /> / BL
              </>
            )}
            <span className="mx-2 text-border">·</span>
            {line.currency}
          </p>
          {overMax?.exceeds && (
            <p className="mt-1 text-xs font-medium text-destructive">
              Gross {formatKgAsTons(overMax.grossKg)}t exceeds max{" "}
              {formatKgAsTons(overMax.capKg)}t
            </p>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0"
          aria-label="Close surcharge breakdown"
          onClick={onClose}
        >
          <X className="size-4" />
        </Button>
      </div>

      {isLcl && (
        <div className="mb-4 rounded-md border border-border/60 bg-muted/30 p-3 text-xs">
          <div className="mb-1 flex items-center gap-2">
            <span className="rounded bg-foreground/10 px-1.5 py-0.5 font-semibold uppercase tracking-wide">
              LCL
            </span>
            <span className="text-muted-foreground">
              Priced per revenue ton ({freightBasis} — the greater of per-CBM and
              per-ton). FRT shown is per W/M unit, not per container.
            </span>
          </div>
          {line.handling_agent && (
            <p className="text-muted-foreground">
              Handling agent:{" "}
              <span className="font-medium text-foreground">
                {line.handling_agent}
              </span>
            </p>
          )}
          {line.route_via && (
            <p className="text-muted-foreground">
              Route via:{" "}
              <span className="font-medium text-foreground">
                {line.route_via}
              </span>
            </p>
          )}
        </div>
      )}

      {groups.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No surcharges recorded for this line.
        </p>
      ) : (
        <div className="flex flex-col gap-4">
          {groups.map((group) => (
            <div key={group.key} className="overflow-x-auto">
              <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </h4>
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border/60 text-left text-xs text-muted-foreground">
                    <th className="pb-2 pr-3 font-medium">Code</th>
                    <th className="pb-2 pr-3 font-medium">Amount</th>
                    <th className="pb-2 pr-3 font-medium">Currency</th>
                    <th className="pb-2 font-medium">Basis</th>
                  </tr>
                </thead>
                <tbody>
                  {group.container.map((s, i) => (
                    <SurchargeRow
                      key={`${s.code}-ctr-${i}`}
                      surcharge={s}
                      overlayByCode={overlayByCode}
                      equipmentSize={line.equipment_size}
                    />
                  ))}
                  {group.bl.map((s, i) => (
                    <SurchargeRow
                      key={`${s.code}-bl-${i}`}
                      surcharge={s}
                      basisSuffix="/ BL"
                      overlayByCode={overlayByCode}
                      equipmentSize={line.equipment_size}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      <Separator className="my-4" />

      <div className="space-y-3">
        <h3 className="text-sm font-semibold">Policies</h3>
        <RateLinePolicySections line={line} />
      </div>
    </section>
  );
}
